/**
 * Shared timezone list used by General Settings and the Backup schedule.
 *
 * Kept in one place so the two pages always offer the same choices. Value is a
 * valid IANA timezone name (used directly by the scheduler's `tz` option).
 */
export interface TimezoneOption {
  value: string;
  label: string;
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: 'Asia/Dhaka', label: 'Asia/Dhaka (Bangladesh)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (India)' },
  { value: 'Asia/Karachi', label: 'Asia/Karachi (Pakistan)' },
  { value: 'Asia/Colombo', label: 'Asia/Colombo (Sri Lanka)' },
  { value: 'Asia/Kathmandu', label: 'Asia/Kathmandu (Nepal)' },
  { value: 'Asia/Thimphu', label: 'Asia/Thimphu (Bhutan)' },
  { value: 'Asia/Yangon', label: 'Asia/Yangon (Myanmar)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (Thailand)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (Singapore)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala Lumpur (Malaysia)' },
  { value: 'Asia/Jakarta', label: 'Asia/Jakarta (Indonesia)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong Kong (Hong Kong)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (China)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (Japan)' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (Korea)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (UAE)' },
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (Saudi Arabia)' },
  { value: 'Asia/Tehran', label: 'Asia/Tehran (Iran)' },
  { value: 'Asia/Jerusalem', label: 'Asia/Jerusalem (Israel)' },
  { value: 'Asia/Istanbul', label: 'Asia/Istanbul (Turkey)' },
  { value: 'Europe/London', label: 'Europe/London (UK)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (France)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (Germany)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (Russia)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (Italy)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (Spain)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (Netherlands)' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich (Switzerland)' },
  { value: 'America/New_York', label: 'America/New York (USA East)' },
  { value: 'America/Chicago', label: 'America/Chicago (USA Central)' },
  { value: 'America/Denver', label: 'America/Denver (USA Mountain)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (USA West)' },
  { value: 'America/Toronto', label: 'America/Toronto (Canada East)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (Canada West)' },
  { value: 'America/Mexico_City', label: 'America/Mexico City (Mexico)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao Paulo (Brazil)' },
  { value: 'America/Buenos_Aires', label: 'America/Buenos Aires (Argentina)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (Sydney)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (Melbourne)' },
  { value: 'Australia/Perth', label: 'Australia/Perth (Perth)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (New Zealand)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

/** Sentinel value used by the dropdown to switch into custom-entry mode. */
export const TIMEZONE_CUSTOM_VALUE = '__custom__';

/** True when `timezone` is not one of the preset options. */
export function isCustomTimezone(timezone: string): boolean {
  return (
    timezone !== '' &&
    !TIMEZONE_OPTIONS.some((option) => option.value === timezone)
  );
}

/**
 * Fallback timezone used when Settings → General has no `hospital_timezone`.
 * Mirrors the backend's default so a missing setting behaves the same on both
 * sides.
 */
export const DEFAULT_HOSPITAL_TIMEZONE = 'Asia/Dhaka';

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

interface ZonedDateTimeParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
}

/**
 * Wall-clock parts for `date` in `timeZone` (IANA name). Returns `null` when the
 * zone is missing or invalid so callers can fall back to the browser's zone.
 */
function zonedDateTimeParts(date: Date, timeZone?: string): ZonedDateTimeParts | null {
  if (!timeZone) return null;

  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);

    const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    const result: ZonedDateTimeParts = {
      year: pick('year'),
      month: pick('month'),
      day: pick('day'),
      hour: pick('hour'),
      minute: pick('minute'),
    };

    return Object.values(result).every(Boolean) ? result : null;
  } catch {
    // Invalid timezone — let the caller fall back to local time.
    return null;
  }
}

/**
 * Current wall-clock time in `timeZone`, formatted for `<input
 * type="datetime-local">` / the dashboard's date-time picker
 * (`YYYY-MM-DDTHH:mm`).
 *
 * Falls back to the browser's local time when the zone is missing or invalid.
 */
export function toDatetimeLocalValueInTimeZone(date: Date, timeZone?: string): string {
  const zoned = zonedDateTimeParts(date, timeZone);
  if (zoned) {
    return `${zoned.year}-${zoned.month}-${zoned.day}T${zoned.hour}:${zoned.minute}`;
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
    date.getHours(),
  )}:${pad2(date.getMinutes())}`;
}

/**
 * The current date/time in `timeZone` split into the picker's two parts:
 * `{ date: 'YYYY-MM-DD', time: 'HH:mm' }`.
 */
export function nowDateTimeLocalInTimeZone(timeZone?: string): { date: string; time: string } {
  const [date, time = '00:00'] = toDatetimeLocalValueInTimeZone(new Date(), timeZone).split('T');
  return { date, time };
}

/**
 * Offset (in ms) to add to a UTC instant to reach the wall-clock time in
 * `timeZone`, e.g. `Asia/Dhaka` → +6h. Evaluated at the given instant so DST
 * transitions are respected. Returns `null` when the zone is invalid.
 */
function timeZoneOffsetMs(instant: Date, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(instant);

    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    const asUTC = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second'),
    );
    return asUTC - instant.getTime();
  } catch {
    return null;
  }
}

/**
 * Converts a `datetime-local` wall-clock string (`YYYY-MM-DDTHH:mm`) interpreted
 * in `timeZone` into an ISO instant string. The mirror of
 * `toDatetimeLocalValueInTimeZone`, so the instant persisted on the server
 * matches the time shown in the field. Falls back to the browser's local
 * interpretation when the zone is missing or invalid.
 */
export function datetimeLocalToISOInTimeZone(value: string, timeZone?: string): string {
  if (!value) return value;
  if (!timeZone) return new Date(value).toISOString();

  const [datePart, timePart = '00:00'] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) {
    return new Date(value).toISOString();
  }

  // Treat the wall-clock parts as if they were UTC, then shift by the zone
  // offset. The offset is looked up twice so a DST boundary lands correctly.
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  const firstOffset = timeZoneOffsetMs(new Date(utcGuess), timeZone);
  if (firstOffset === null) return new Date(value).toISOString();
  const offset = timeZoneOffsetMs(new Date(utcGuess - firstOffset), timeZone);
  if (offset === null) return new Date(value).toISOString();
  return new Date(utcGuess - offset).toISOString();
}
